import { Logger } from "../utils/Logger.js";

/**
 * Service for coordinate conversion between world and screen space
 */
export class CoordinateConverter {
	constructor(viewer) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("CoordinateConverter");
	}

	/**
	 * Convert world coordinates to screen coordinates
	 * @param {Object} worldPos - World position {x, y, z}
	 * @returns {Object|null} Screen position {x, y} or null
	 */
	worldToScreen(worldPos) {
		if (!worldPos || !this.viewer) {
			this.logger.warn("Invalid world position or viewer not available");
			return null;
		}

		try {
			const screenPos = this.viewer.worldToClient(worldPos);

			if (this.isValidScreenPosition(screenPos)) {
				return screenPos;
			} else {
				this.logger.warn(
					"World to screen conversion returned invalid position",
					screenPos
				);
				return null;
			}
		} catch (error) {
			this.logger.error("Error converting world to screen coordinates", error);
			return null;
		}
	}

	/**
	 * Convert screen coordinates to world coordinates
	 * @param {number} x - Screen X coordinate
	 * @param {number} y - Screen Y coordinate
	 * @returns {Object|null} World position or null
	 */
	screenToWorld(x, y) {
		if (!this.viewer || typeof x !== "number" || typeof y !== "number") {
			this.logger.warn("Invalid screen coordinates or viewer not available");
			return null;
		}

		try {
			const worldPos = this.viewer.clientToWorld(x, y);

			if (worldPos) {
				this.logger.debug(
					`Converted screen (${x}, ${y}) to world ${JSON.stringify(worldPos)}`
				);
				return worldPos;
			} else {
				this.logger.warn(`Screen to world conversion failed for (${x}, ${y})`);
				return null;
			}
		} catch (error) {
			this.logger.error("Error converting screen to world coordinates", error);
			return null;
		}
	}

	/**
	 * Convert model coordinates to client coordinates (for 2D views)
	 * @param {Object} modelPos - Model position {x, y} or {x, y, z}
	 * @returns {Object|null} Client position {x, y} or null
	 */
	modelToClient(modelPos) {
		if (!modelPos || !this.viewer) {
			this.logger.warn("Invalid model position or viewer not available");
			return null;
		}

		try {
			let clientPos;

			// Check if this is a 2D model
			const model = this.viewer.model;
			if (model && model.is2d()) {
				// For 2D models, we might need to handle coordinate conversion differently
				// Try using the camera's world to screen conversion
				const camera = this.viewer.impl.camera;
				const canvas = this.viewer.impl.canvas;

				if (camera && canvas) {
					// Create a THREE.Vector3 from the model position
					const modelVector = new window.THREE.Vector3(
						modelPos.x,
						modelPos.y,
						modelPos.z || 0
					);

					// Project to screen coordinates
					const screenVector = modelVector.clone();
					screenVector.project(camera);

					// Convert normalized device coordinates to screen coordinates
					clientPos = {
						x: ((screenVector.x + 1) * canvas.clientWidth) / 2,
						y: ((-screenVector.y + 1) * canvas.clientHeight) / 2,
					};
				} else {
					// Fallback to worldToClient
					clientPos = this.viewer.worldToClient(modelPos);
				}
			} else {
				// For 3D models, use worldToClient
				clientPos = this.viewer.worldToClient(modelPos);
			}

			if (this.isValidScreenPosition(clientPos)) {
				this.logger.debug(
					`Converted model ${JSON.stringify(modelPos)} to client ${JSON.stringify(clientPos)}`
				);
				return clientPos;
			} else {
				this.logger.warn(
					"Model to client conversion returned invalid position",
					clientPos
				);
				return modelPos; // Fallback to original position
			}
		} catch (error) {
			this.logger.error("Error converting model to client coordinates", error);
			return modelPos; // Fallback to original position
		}
	}

	/**
	 * Get screen bounds of a 3D bounding box
	 * @param {Object} bbox - THREE.Box3 bounding box
	 * @returns {Object|null} Screen bounds {min: {x, y}, max: {x, y}} or null
	 */
	getBoundingBoxScreenBounds(bbox) {
		if (!bbox || bbox.isEmpty()) {
			this.logger.warn("Invalid or empty bounding box provided");
			return null;
		}

		try {
			// Get all 8 corners of the bounding box
			const corners = [
				{ x: bbox.min.x, y: bbox.min.y, z: bbox.min.z },
				{ x: bbox.max.x, y: bbox.min.y, z: bbox.min.z },
				{ x: bbox.min.x, y: bbox.max.y, z: bbox.min.z },
				{ x: bbox.max.x, y: bbox.max.y, z: bbox.min.z },
				{ x: bbox.min.x, y: bbox.min.y, z: bbox.max.z },
				{ x: bbox.max.x, y: bbox.min.y, z: bbox.max.z },
				{ x: bbox.min.x, y: bbox.max.y, z: bbox.max.z },
				{ x: bbox.max.x, y: bbox.max.y, z: bbox.max.z },
			];

			let minX = Infinity,
				minY = Infinity;
			let maxX = -Infinity,
				maxY = -Infinity;
			let validPoints = 0;

			// Convert all corners to screen coordinates
			for (const corner of corners) {
				const screenPos = this.worldToScreen(corner);
				if (screenPos) {
					minX = Math.min(minX, screenPos.x);
					minY = Math.min(minY, screenPos.y);
					maxX = Math.max(maxX, screenPos.x);
					maxY = Math.max(maxY, screenPos.y);
					validPoints++;
				}
			}

			if (validPoints === 0) {
				this.logger.warn("No valid screen points found for bounding box");
				return null;
			}

			const screenBounds = {
				min: { x: minX, y: minY },
				max: { x: maxX, y: maxY },
				center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
			};

			this.logger.debug(
				`Calculated screen bounds for bounding box`,
				screenBounds
			);
			return screenBounds;
		} catch (error) {
			this.logger.error("Error calculating bounding box screen bounds", error);
			return null;
		}
	}

	/**
	 * Check if screen position is valid
	 * @param {Object} position - Position object {x, y}
	 * @returns {boolean} True if valid
	 */
	isValidScreenPosition(position) {
		return (
			position &&
			typeof position.x === "number" &&
			typeof position.y === "number" &&
			!isNaN(position.x) &&
			!isNaN(position.y) &&
			isFinite(position.x) &&
			isFinite(position.y)
		);
	}

	/**
	 * Get viewport dimensions
	 * @returns {Object} Viewport size {width, height}
	 */
	getViewportSize() {
		const container = this.viewer?.container;
		if (!container) {
			return { width: 0, height: 0 };
		}

		return {
			width: container.clientWidth,
			height: container.clientHeight,
		};
	}

	/**
	 * Get center of viewport
	 * @returns {Object} Center position {x, y}
	 */
	getViewportCenter() {
		const size = this.getViewportSize();
		return {
			x: size.width / 2,
			y: size.height / 2,
		};
	}

	/**
	 * Check if position is within viewport bounds
	 * @param {Object} position - Position {x, y}
	 * @param {number} tolerance - Tolerance in pixels
	 * @returns {boolean} True if within bounds
	 */
	isWithinViewport(position, tolerance = 0) {
		if (!this.isValidScreenPosition(position)) {
			return false;
		}

		const size = this.getViewportSize();
		return (
			position.x >= -tolerance &&
			position.x <= size.width + tolerance &&
			position.y >= -tolerance &&
			position.y <= size.height + tolerance
		);
	}
}
